import {
	Button,
	Card,
	CardBody,
	CardFooter,
	CardHeader,
	CardHeaderActions,
	CardHeading,
	I18nDiscardLocaleButton,
	I18nLocaleSwitcher,
	LinkButton,
	Loading,
	useI18nForm,
} from "@dgs/core";
import { FC, ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { EventAddressClearButton } from "./EventAddressClearButton";
import { emptyTranslation } from "./eventStepperUtils";

interface Props {
	variant?: "elevated" | "outlined";
	title: string;
	children: ReactNode;
	prevLink?: string;
	onNextClick?: () => void;
	onSubmitClick?: () => void;
	showLanguageSwitcher: boolean;
	isLoading?: boolean;
	canClearAddress?: boolean;
}

const StyledCardFooter = styled(CardFooter)`
	display: flex;
`;

const Spacer = styled.div`
	flex: 1;
`;

export const EventFormCard: FC<Props> = ({
	variant,
	onNextClick,
	onSubmitClick,
	prevLink,
	title,
	children,
	showLanguageSwitcher,
	isLoading = false,
	canClearAddress,
}) => {
	const { t } = useTranslation();
	const { currentLanguage, fallbackLanguage } = useI18nForm();

	useEffect(() => {
		const listener = (e: KeyboardEvent) => {
			if (e.key === "Enter") {
				onNextClick && onNextClick();
				onSubmitClick && onSubmitClick();
			}
		};
		document.addEventListener("keyup", listener);

		return () => document.removeEventListener("keyup", listener);
	}, [onNextClick, onSubmitClick]);

	return (
		<Card $variant={variant}>
			<CardHeader>
				<CardHeading>{title}</CardHeading>
				<CardHeaderActions>
					{showLanguageSwitcher && <I18nLocaleSwitcher emptyTranslation={emptyTranslation} />}
					{canClearAddress && <EventAddressClearButton />}
				</CardHeaderActions>
			</CardHeader>
			<CardBody>{children}</CardBody>
			<StyledCardFooter>
				{prevLink && (
					<LinkButton to={prevLink} type="button">
						{t("Previous")}
					</LinkButton>
				)}
				{showLanguageSwitcher && currentLanguage !== fallbackLanguage && <I18nDiscardLocaleButton />}
				<Spacer />
				{onNextClick && (
					<Button
						prefix={isLoading && <Loading variant="tiny" color="current" />}
						disabled={isLoading}
						color="primary"
						onClick={onNextClick}
						type="button"
					>
						{t("Next")}
					</Button>
				)}
				{onSubmitClick && (
					<Button
						prefix={isLoading && <Loading variant="tiny" color="current" />}
						disabled={isLoading}
						color="primary"
						onClick={onSubmitClick}
						type="button"
					>
						{t("Submit")}
					</Button>
				)}
			</StyledCardFooter>
		</Card>
	);
};
